import React, {
    useEffect,
    useRef,
    useState
} from "react";

import API_URL from "../api/API";
import { Link } from "react-router-dom";


const STORAGE_KEY =
    "hikmat_conversation_id";


const Hikmat = () => {

    const [conversationId, setConversationId] =
        useState(() =>
            localStorage.getItem(
                STORAGE_KEY
            )
        );

    const [messages, setMessages] =
        useState([]);

    const [input, setInput] =
        useState("");

    const [isStreaming, setIsStreaming] =
        useState(false);

    const [error, setError] =
        useState("");

    const textareaRef =
        useRef(null);

    const bottomRef =
        useRef(null);

    const abortControllerRef =
        useRef(null);


    useEffect(() => {

        bottomRef.current?.scrollIntoView({
            behavior:
                isStreaming
                    ? "auto"
                    : "smooth"
        });

    }, [messages, isStreaming]);


    useEffect(() => {

        return () => {

            abortControllerRef.current?.abort();

        };

    }, []);


    const resizeTextarea =
        () => {

            const textarea =
                textareaRef.current;

            if (!textarea) {
                return;
            }

            textarea.style.height =
                "auto";

            textarea.style.height =
                `${Math.min(
                    textarea.scrollHeight,
                    160
                )}px`;

        };


    useEffect(() => {

        resizeTextarea();

    }, [input]);


    const appendAssistantText =
        (text) => {

            setMessages(
                (currentMessages) => {

                    const updated =
                        [...currentMessages];

                    const lastMessage =
                        updated[
                            updated.length - 1
                        ];

                    if (
                        lastMessage &&
                        lastMessage.role ===
                            "assistant" &&
                        lastMessage.streaming
                    ) {

                        updated[
                            updated.length - 1
                        ] = {

                            ...lastMessage,

                            content:
                                lastMessage.content +
                                text,

                        };

                    }

                    return updated;

                }
            );

        };


    const finishAssistantMessage =
        () => {

            setMessages(
                (currentMessages) =>
                    currentMessages.map(
                        (message, index) => {

                            if (
                                index ===
                                    currentMessages.length -
                                        1 &&
                                message.role ===
                                    "assistant"
                            ) {

                                return {

                                    ...message,

                                    streaming:
                                        false,

                                };

                            }

                            return message;

                        }
                    )
            );

        };


    const processSSEEvent =
        (
            eventName,
            eventData
        ) => {

            if (!eventData) {
                return;
            }

            let data;

            try {

                data =
                    JSON.parse(
                        eventData
                    );

            } catch {

                return;

            }


            if (
                eventName ===
                "conversation"
            ) {

                if (
                    data.conversationId
                ) {

                    setConversationId(
                        data.conversationId
                    );

                    localStorage.setItem(
                        STORAGE_KEY,
                        data.conversationId
                    );

                }

            }


            if (
                eventName ===
                "delta"
            ) {

                if (data.text) {

                    appendAssistantText(
                        data.text
                    );

                }

            }


            if (
                eventName ===
                "error"
            ) {

                throw new Error(
                    data.error ||
                    "The response was interrupted."
                );

            }

        };


    const sendMessage =
        async () => {

            const cleanMessage =
                input.trim();

            if (
                !cleanMessage ||
                isStreaming
            ) {
                return;
            }

            setError("");

            setInput("");

            setIsStreaming(true);


            setMessages(
                (currentMessages) => [

                    ...currentMessages,

                    {
                        role:
                            "user",

                        content:
                            cleanMessage,
                    },

                    {
                        role:
                            "assistant",

                        content:
                            "",

                        streaming:
                            true,
                    },

                ]
            );


            const controller =
                new AbortController();

            abortControllerRef.current =
                controller;


            try {

                const response =
                    await fetch(
                        API_URL
                            .POST_HIKMAT_MESSAGE,
                        {
                            method:
                                "POST",

                            headers: {
                                "Content-Type":
                                    "application/json",
                            },

                            body:
                                JSON.stringify({

                                    conversationId:
                                        conversationId ||
                                        undefined,

                                    message:
                                        cleanMessage,

                                }),

                            signal:
                                controller.signal,
                        }
                    );


                if (!response.ok) {

                    let message =
                        "Something went wrong. Please try again.";

                    try {

                        const data =
                            await response.json();

                        if (data.error) {

                            message =
                                data.error;

                        }

                    } catch {
                        // Keep fallback message.
                    }

                    throw new Error(
                        message
                    );

                }


                if (!response.body) {

                    throw new Error(
                        "Streaming is not supported by this browser."
                    );

                }


                const reader =
                    response.body
                        .getReader();

                const decoder =
                    new TextDecoder();

                let buffer =
                    "";


                while (true) {

                    const {
                        value,
                        done,
                    } =
                        await reader.read();

                    if (done) {
                        break;
                    }

                    buffer +=
                        decoder.decode(
                            value,
                            {
                                stream:
                                    true,
                            }
                        );

                    buffer =
                        buffer.replace(
                            /\r\n/g,
                            "\n"
                        );

                    const events =
                        buffer.split(
                            "\n\n"
                        );

                    buffer =
                        events.pop() ||
                        "";


                    for (
                        const eventBlock
                        of events
                    ) {

                        const lines =
                            eventBlock.split(
                                "\n"
                            );

                        let eventName =
                            "message";

                        const dataLines =
                            [];


                        for (
                            const line
                            of lines
                        ) {

                            if (
                                line.startsWith(
                                    "event:"
                                )
                            ) {

                                eventName =
                                    line
                                        .slice(6)
                                        .trim();

                            }

                            if (
                                line.startsWith(
                                    "data:"
                                )
                            ) {

                                dataLines.push(
                                    line
                                        .slice(5)
                                        .trimStart()
                                );

                            }

                        }


                        processSSEEvent(
                            eventName,
                            dataLines.join(
                                "\n"
                            )
                        );

                    }

                }


                buffer +=
                    decoder.decode();

                buffer =
                    buffer.replace(
                        /\r\n/g,
                        "\n"
                    );


                if (buffer.trim()) {

                    const lines =
                        buffer.split(
                            "\n"
                        );

                    let eventName =
                        "message";

                    const dataLines =
                        [];


                    for (
                        const line
                        of lines
                    ) {

                        if (
                            line.startsWith(
                                "event:"
                            )
                        ) {

                            eventName =
                                line
                                    .slice(6)
                                    .trim();

                        }

                        if (
                            line.startsWith(
                                "data:"
                            )
                        ) {

                            dataLines.push(
                                line
                                    .slice(5)
                                    .trimStart()
                            );

                        }

                    }


                    processSSEEvent(
                        eventName,
                        dataLines.join(
                            "\n"
                        )
                    );

                }


                finishAssistantMessage();


            } catch (err) {

                if (
                    err.name !==
                    "AbortError"
                ) {

                    setError(
                        err.message ||
                        "Something went wrong. Please try again."
                    );


                    setMessages(
                        (currentMessages) => {

                            const updated =
                                [...currentMessages];

                            const lastMessage =
                                updated[
                                    updated.length -
                                        1
                                ];

                            if (
                                lastMessage?.role ===
                                    "assistant" &&
                                lastMessage?.streaming &&
                                !lastMessage.content
                            ) {

                                updated.pop();

                            }

                            return updated;

                        }
                    );

                }


            } finally {

                setIsStreaming(false);

                abortControllerRef.current =
                    null;


                setTimeout(
                    () => {

                        textareaRef
                            .current
                            ?.focus();

                    },
                    50
                );

            }

        };


    const handleKeyDown =
        (event) => {

            if (
                event.key ===
                    "Enter" &&
                !event.shiftKey
            ) {

                event.preventDefault();

                sendMessage();

            }

        };


    const startNewConversation =
        () => {

            if (isStreaming) {
                return;
            }

            localStorage.removeItem(
                STORAGE_KEY
            );

            setConversationId(
                null
            );

            setMessages([]);

            setInput("");

            setError("");


            setTimeout(
                () =>
                    textareaRef
                        .current
                        ?.focus(),
                50
            );

        };


    const renderMessageContent =
        (content) => {

            const paragraphs =
                content.split(
                    "\n"
                );

            return paragraphs.map(
                (line, index) => {

                    if (!line.trim()) {

                        return (
                            <div
                                key={index}
                                className="h-3"
                            />
                        );

                    }


                    const parts =
                        line.split(
                            /(\*\*.*?\*\*)/g
                        );


                    return (
                        <p
                            key={index}
                            className="leading-7"
                        >

                            {
                                parts.map(
                                    (
                                        part,
                                        partIndex
                                    ) => {

                                        if (
                                            part.startsWith(
                                                "**"
                                            ) &&
                                            part.endsWith(
                                                "**"
                                            )
                                        ) {

                                            return (
                                                <strong
                                                    key={
                                                        partIndex
                                                    }
                                                    className="font-semibold text-stone-900"
                                                >
                                                    {
                                                        part.slice(
                                                            2,
                                                            -2
                                                        )
                                                    }
                                                </strong>
                                            );

                                        }


                                        return (
                                            <React.Fragment
                                                key={
                                                    partIndex
                                                }
                                            >
                                                {
                                                    part
                                                }
                                            </React.Fragment>
                                        );

                                    }
                                )
                            }

                        </p>
                    );

                }
            );

        };


    const hasConversation =
        messages.length > 0;


    return (

        <div className="min-h-[100dvh] bg-[#f7f5ef] text-stone-900">

            <div className="flex min-h-[100dvh] flex-col">


                {/* Header */}

                <header className="flex h-16 shrink-0 items-center justify-between border-b border-stone-900/5 px-5 sm:h-[72px] sm:px-8 lg:px-12">

                <button
                        type="button"
                        onClick={startNewConversation}
                        disabled={isStreaming}
                        title="Start a new conversation"
                        className="flex items-center gap-3 text-left transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                    >

                        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-900/15 font-serif text-lg">
                            H
                        </div>

                        <div>

                            <div className="font-serif text-xl leading-none tracking-tight">
                                Hikmah
                            </div>

                            <div className="mt-1 hidden text-[10px] uppercase tracking-[0.22em] text-stone-500 sm:block">
                                Wisdom from the adages of Imam Ali
                            </div>

                        </div>

                    </button>


                    <div className="flex items-center gap-2 sm:gap-3">

                        <Link
                            to="/about"
                            className="rounded-full px-3 py-2 text-xs font-medium text-stone-500 transition hover:text-stone-900 sm:text-sm"
                        >
                            About
                        </Link>

                        {
                            hasConversation && (

                                <button
                                    type="button"
                                    onClick={startNewConversation}
                                    disabled={isStreaming}
                                    className="rounded-full border border-stone-900/10 bg-white/60 px-3 py-2 text-xs font-medium text-stone-600 transition hover:border-stone-900/20 hover:bg-white hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40 sm:px-4 sm:text-sm"
                                >
                                    New conversation
                                </button>

                            )
                        }

                        </div>

                </header>


                {
                    !hasConversation ? (

                        /*
                         * Landing state
                         */

                        <main className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8">

                            <div className="w-full max-w-3xl">

                                <div className="mb-9 text-center sm:mb-12">

                                    <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-stone-900/10 bg-white/50 font-serif text-2xl shadow-sm sm:h-16 sm:w-16">
                                        H
                                    </div>


                                    <h1 className="mx-auto max-w-2xl font-serif text-[2.5rem] leading-[1.08] tracking-[-0.035em] text-stone-900 sm:text-5xl lg:text-[3.5rem]">

                                        Wisdom for the situations
                                        life puts you in.

                                    </h1>


                                    <p className="mx-auto mt-5 max-w-xl text-[15px] leading-7 text-stone-500 sm:text-base">

                                        Share what is on your mind.
                                        Hikmah reflects on it through
                                        the collected adages of Imam Ali,
                                        bringing their wisdom into the
                                        realities of everyday life.

                                    </p>

                                </div>


                                <Composer
                                    input={input}
                                    setInput={
                                        setInput
                                    }
                                    textareaRef={
                                        textareaRef
                                    }
                                    onKeyDown={
                                        handleKeyDown
                                    }
                                    onSend={
                                        sendMessage
                                    }
                                    isStreaming={
                                        isStreaming
                                    }
                                    large
                                />


                                <div className="mt-5 text-center text-[11px] leading-5 text-stone-400 sm:text-xs">

                                    <div>
                                        A space for reflection, not a
                                        substitute for professional,
                                        medical or emergency advice.
                                    </div>


                                    <div className="mt-4">

                                        <a
                                            href="https://www.softronikx.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-medium text-stone-500 transition hover:text-stone-700"
                                        >
                                            Softronikx Technologies
                                        </a>

                                        <div className="mt-0.5 text-[10px] tracking-wide text-stone-400">
                                            AI Application Development Company
                                        </div>

                                    </div>

                                </div>

                            </div>

                        </main>

                    ) : (

                        /*
                         * Conversation state
                         */

                        <main className="flex min-h-0 flex-1 flex-col">

                            <div className="flex-1 overflow-y-auto">

                                <div className="mx-auto w-full max-w-3xl px-5 pb-40 pt-8 sm:px-8 sm:pt-12">

                                    {
                                        messages.map(
                                            (
                                                message,
                                                index
                                            ) => (

                                                <div
                                                    key={
                                                        index
                                                    }
                                                    className={
                                                        message.role ===
                                                        "user"
                                                            ?
                                                            "mb-8 flex justify-end sm:mb-10"
                                                            :
                                                            "mb-10 sm:mb-12"
                                                    }
                                                >

                                                    {
                                                        message.role ===
                                                        "user" ? (

                                                            <div className="max-w-[88%] rounded-[24px] rounded-br-md bg-stone-900 px-5 py-3.5 text-[15px] leading-6 text-stone-50 shadow-sm sm:max-w-[78%] sm:px-6 sm:py-4 sm:text-base">
                                                                {
                                                                    message.content
                                                                }
                                                            </div>

                                                        ) : (

                                                            <div className="flex gap-4 sm:gap-5">

                                                                <div className="mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-900/10 bg-white font-serif text-base shadow-sm sm:flex">
                                                                    H
                                                                </div>


                                                                <div className="min-w-0 flex-1">

                                                                    <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
                                                                        Hikmah
                                                                    </div>


                                                                    {
                                                                        message.content ? (

                                                                            <div className="text-[15px] leading-7 text-stone-700 sm:text-[16px] sm:leading-8">

                                                                                {
                                                                                    renderMessageContent(
                                                                                        message.content
                                                                                    )
                                                                                }


                                                                                {
                                                                                    message.streaming && (

                                                                                        <span className="ml-1 inline-block h-[18px] w-[2px] animate-pulse bg-stone-500 align-middle" />

                                                                                    )
                                                                                }

                                                                            </div>

                                                                        ) : (

                                                                            <div className="flex items-center gap-1.5 py-2">

                                                                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-stone-400" />

                                                                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-stone-400 [animation-delay:150ms]" />

                                                                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-stone-400 [animation-delay:300ms]" />

                                                                            </div>

                                                                        )
                                                                    }

                                                                </div>

                                                            </div>

                                                        )
                                                    }

                                                </div>

                                            )
                                        )
                                    }


                                    {
                                        error && (

                                            <div className="mb-8 rounded-2xl border border-red-900/10 bg-red-50 px-4 py-3 text-sm leading-6 text-red-700">
                                                {error}
                                            </div>

                                        )
                                    }


                                    <div
                                        ref={
                                            bottomRef
                                        }
                                    />

                                </div>

                            </div>


                            {/* Fixed conversation composer */}

                            <div className="fixed inset-x-0 bottom-0 z-20 bg-gradient-to-t from-[#f7f5ef] via-[#f7f5ef] to-transparent px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-8 sm:px-8 sm:pb-6">

                                <div className="mx-auto max-w-3xl">

                                    <Composer
                                        input={
                                            input
                                        }
                                        setInput={
                                            setInput
                                        }
                                        textareaRef={
                                            textareaRef
                                        }
                                        onKeyDown={
                                            handleKeyDown
                                        }
                                        onSend={
                                            sendMessage
                                        }
                                        isStreaming={
                                            isStreaming
                                        }
                                    />


                                    <div className="mt-2 text-center text-[10px] text-stone-400 sm:text-[11px]">
                                        Hikmah offers reflection grounded in the collected adages of Imam Ali.
                                    </div>

                                </div>

                            </div>

                        </main>

                    )
                }

            </div>

        </div>

    );

};


const Composer = ({
    input,
    setInput,
    textareaRef,
    onKeyDown,
    onSend,
    isStreaming,
    large = false,
}) => {

    return (

        <div
            className={
                `relative border border-stone-900/10 bg-white shadow-[0_12px_45px_rgba(28,25,23,0.08)] transition focus-within:border-stone-900/20 focus-within:shadow-[0_16px_55px_rgba(28,25,23,0.11)] ${
                    large
                        ?
                        "rounded-[28px]"
                        :
                        "rounded-[24px]"
                }`
            }
        >

            <textarea
                ref={
                    textareaRef
                }
                value={
                    input
                }
                onChange={
                    (event) =>
                        setInput(
                            event.target.value
                        )
                }
                onKeyDown={
                    onKeyDown
                }
                disabled={
                    isStreaming
                }
                rows={1}
                maxLength={4000}
                placeholder={
                    large
                        ?
                        "What's on your mind?"
                        :
                        "Ask a follow-up..."
                }
                className={
                    `block max-h-40 min-h-[58px] w-full resize-none bg-transparent px-5 pb-16 pt-5 text-[16px] leading-6 text-stone-800 outline-none placeholder:text-stone-400 disabled:cursor-not-allowed disabled:opacity-60 sm:px-6 ${
                        large
                            ?
                            "sm:min-h-[132px] sm:pt-6"
                            :
                            ""
                    }`
                }
            />


            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-3.5 pb-3.5 sm:px-4 sm:pb-4">

                <div className="pl-1 text-[10px] text-stone-300 sm:text-[11px]">

                    {
                        input.length >
                        3600
                            ?
                            `${input.length}/4000`
                            :
                            "Shift + Enter for a new line"
                    }

                </div>


                <button
                    type="button"
                    onClick={
                        onSend
                    }
                    disabled={
                        !input.trim() ||
                        isStreaming
                    }
                    aria-label="Send message"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-stone-900 text-white shadow-sm transition hover:scale-[1.03] hover:bg-black active:scale-95 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400 disabled:shadow-none"
                >

                    {
                        isStreaming ? (

                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />

                        ) : (

                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                className="h-[18px] w-[18px]"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 19V5" />
                                <path d="m6 11 6-6 6 6" />
                            </svg>

                        )
                    }

                </button>

            </div>

        </div>

    );

};


export default Hikmat;